/**
 * Поток возражений окна (`GET /agents/objections/stream`) и его окно периода —
 * у экрана «Возражения» и у вкладки «Продажи» «Голоса клиента» (#2060). Вкладка
 * отдаёт свой запрос и сводке, и разделу «Возражения»: второго наблюдателя со
 * своим окном нет, и раздел, открытый позже, считает то же окно, что сводка.
 */
import { keepPreviousData, useQuery, type QueryFilters } from '@tanstack/react-query';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useLockedDemoAsked } from '@/contexts/LockedDemoContext';
import { sortedProjectIdsKey } from '@/lib/project-filter';
import { retryTransientOnly } from '@/lib/query-retry';
import type { VoiceSearch } from '../voice/voice-search';
import type { ObjectionsSearch } from './objections-search';
import { useStreamWindow } from '../stream-filter/useStreamWindow';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { keepsThemePlaceholder, themeFilteredQueries } from '../voice/theme-filter-key';

/** The theme is the last element of the stream's key. */
const THEME_FROM_END = 1;
// The demo mode, before the marks and the theme.
const MODE_FROM_END = 3;

/**
 * The stream's query key and request — one for the hook and the Voice of
 * Customer section's tab prefetch (#2316), so the Sales tab takes the warmed
 * answer.
 */
export function objectionStreamRequest({
  apiClient,
  orgId,
  stream,
  includeDemo,
  lockedDemo,
  category,
  themeCode,
  marks,
}: {
  apiClient: ReturnType<typeof useAuth>['apiClient'];
  orgId: string | null;
  stream: ReturnType<typeof useStreamWindow>;
  includeDemo: 'yes' | 'no';
  /**
   * The screen shows the demo of a closed feature (#2816): the demo answer and
   * the organization's own never share a cache entry.
   */
  lockedDemo: boolean;
  category: ObjectionsSearch['category'];
  themeCode: string | undefined;
  /**
   * The Voice of Customer section's «What changed» reads the server's mark of
   * each code (#2552); the Objections screen does not, and the server counts
   * the marks only when asked.
   */
  marks: boolean;
}) {
  const { dateRange, projectIds, callCategory } = stream;
  const queryKey = [
    'agents',
    'objection-stream',
    orgId,
    dateRange.periodStart,
    dateRange.periodEnd,
    sortedProjectIdsKey(projectIds),
    callCategory,
    includeDemo,
    lockedDemo ? 'demo' : 'own',
    marks,
    // Last: an edit of the theme set drops every narrowed stream by it.
    themeCode ?? null,
  ] as const;
  return {
    queryKey,
    queryFn: () =>
      apiClient.agents.getObjectionStream({
        periodStart: dateRange.periodStart,
        periodEnd: dateRange.periodEnd,
        projectIds,
        callCategory: category,
        includeDemo,
        ...(themeCode ? { themeCode } : {}),
        ...(marks ? { marks: 'true' as const } : {}),
      }),
  };
}

export function useObjectionStream(
  search: ObjectionsSearch & Pick<VoiceSearch, 'voiceTheme'>,
  onSearchChange: (patch: Partial<ObjectionsSearch>) => void,
  inVoiceSection: boolean
) {
  const { apiClient, orgId } = useAuth();
  // The organization's demo policy, as every dashboard passes it: demo calls
  // are counted only while the org has no real ones. The daily digest's link
  // opens the same numbers for that reason — an org that gets the digest has
  // real records, so its policy is 'no'. In the Voice of Customer section the
  // monthly email's window counts without demo calls, as the section does.
  const { includeDemo, ready: demoReady } = useStreamDemoFilter(
    search,
    inVoiceSection ? 'voice-section' : 'objections'
  );
  const streamWindow = useStreamWindow(search, onSearchChange);
  const lockedDemo = useLockedDemoAsked();
  // The section's theme filter (#2229) — never on the Objections screen.
  const themeCode = inVoiceSection ? search.voiceTheme : undefined;

  const request = objectionStreamRequest({
    apiClient,
    orgId,
    stream: streamWindow,
    includeDemo,
    lockedDemo,
    category: search.category,
    themeCode,
    marks: inVoiceSection,
  });
  const { queryKey } = request;
  const query = useQuery({
    queryKey,
    queryFn: request.queryFn,
    enabled: Boolean(orgId) && demoReady,
    // Keep the previous numbers while a filter changes — but never across
    // organizations: that would show one tenant's customer quotes and operator
    // names under another's name until the new request lands.
    // Nor across themes: another theme's numbers are not this filter's.
    placeholderData: (previous, previousQuery) =>
      keepsThemePlaceholder(
        previousQuery?.queryKey,
        queryKey,
        THEME_FROM_END,
        MODE_FROM_END
      )
        ? keepPreviousData(previous)
        : undefined,
    retry: retryTransientOnly,
  });
  return { query, streamWindow };
}

/**
 * Every stream an organization has in the cache narrowed by a theme: an edit of
 * the theme set drops them with the section's (`voiceThemeFilteredFilter`).
 */
export function objectionStreamThemeFilter(orgId: string | null): QueryFilters {
  return themeFilteredQueries(['agents', 'objection-stream', orgId], THEME_FROM_END);
}
